import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Mail, Save, Send, CheckCircle2, XCircle, Plus, Trash2, RefreshCw, AlertTriangle, Lock } from "lucide-react";
import { toast } from "sonner";

const ENC_OPTIONS = [
  { value: "tls", label: "TLS / STARTTLS (Port 587)" },
  { value: "ssl", label: "SSL (Port 465)" },
  { value: "none", label: "Keine Verschlüsselung (nicht empfohlen)" },
];

const PRESETS = [
  { name: "Microsoft 365 / Outlook", host: "smtp.office365.com", port: 587, encryption: "tls" },
  { name: "Gmail (App-Passwort)", host: "smtp.gmail.com", port: 587, encryption: "tls" },
  { name: "Yahoo Mail", host: "smtp.mail.yahoo.com", port: 587, encryption: "tls" },
  { name: "GMX / Web.de", host: "mail.gmx.net", port: 587, encryption: "tls" },
  { name: "1&1 IONOS", host: "smtp.ionos.de", port: 587, encryption: "tls" },
];

export default function EmailSmsConfig() {
  const [cfg, setCfg] = useState({
    enabled: false, host: "", port: 587, encryption: "tls",
    username: "", password: "", from_email: "", from_name: "MUNJA Trans GmbH",
    password_set: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [recipients, setRecipients] = useState({ meldung: [], tanken: [] });
  const [newMeldung, setNewMeldung] = useState("");
  const [newTanken, setNewTanken] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/settings/smtp"),
        api.get("/settings/email-recipients").catch(() => ({ data: { meldung: [], tanken: [] } })),
      ]);
      setCfg(c);
      setRecipients(r);
    } catch (e) {
      toast.error("Konfiguration konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function patch(k, v) {
    setCfg(c => ({ ...c, [k]: v }));
    setTestResult(null);
  }

  function applyPreset(p) {
    setCfg(c => ({ ...c, host: p.host, port: p.port, encryption: p.encryption }));
    setTestResult(null);
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/smtp", cfg);
      setCfg(data);
      toast.success("SMTP-Einstellungen gespeichert");
    } catch (e) {
      toast.error(`Speichern fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function testConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/smtp/test-connection", cfg);
      setTestResult(data);
      if (data.ok) toast.success("Verbindung zum SMTP-Server erfolgreich");
      else toast.error(`Verbindung fehlgeschlagen: ${data.error}`);
    } catch (e) {
      const err = e.response?.data?.detail || e.message;
      setTestResult({ ok: false, error: err });
      toast.error(`Test fehlgeschlagen: ${err}`);
    } finally {
      setTesting(false);
    }
  }

  async function sendTestEmail() {
    if (!testTo || !testTo.includes("@")) {
      toast.error("Bitte gültige E-Mail-Adresse für Test eingeben");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/settings/smtp/test-send", {
        ...cfg, to: testTo,
      });
      if (data.ok) {
        toast.success(`Test-E-Mail an ${testTo} gesendet ✓`);
        await load();
      } else {
        toast.error(`Versand fehlgeschlagen: ${data.error}`);
      }
    } catch (e) {
      toast.error(`Versand fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  }

  async function saveRecipients(next) {
    try {
      const { data } = await api.put("/settings/email-recipients", next);
      setRecipients(data);
    } catch (e) {
      toast.error(`Speichern fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
    }
  }
  function addRecipient(kind, value) {
    const v = (value || "").trim().toLowerCase();
    if (!v || !v.includes("@")) {
      toast.error("Ungültige E-Mail-Adresse");
      return;
    }
    const next = { ...recipients, [kind]: [...new Set([...(recipients[kind] || []), v])] };
    saveRecipients(next);
    if (kind === "meldung") setNewMeldung("");
    if (kind === "tanken") setNewTanken("");
  }
  function removeRecipient(kind, value) {
    const next = { ...recipients, [kind]: (recipients[kind] || []).filter(x => x !== value) };
    saveRecipients(next);
  }

  if (loading) {
    return <div className="text-sm text-zinc-500">Lade Konfiguration…</div>;
  }

  return (
    <div className="space-y-8" data-testid="email-sms-config">
      {/* ── SMTP Einstellungen ──────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4 pb-2 border-b border-zinc-800">
          <h3 className="text-sm font-semibold text-blue-400 inline-flex items-center gap-2">
            <Mail className="w-4 h-4" /> E-Mail Einstellungen (SMTP)
          </h3>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="smtp-enabled-wrap">
            <span className="text-xs text-zinc-400">Aktiv</span>
            <input
              type="checkbox"
              checked={!!cfg.enabled}
              onChange={(e) => patch("enabled", e.target.checked)}
              data-testid="smtp-enabled"
              className="w-9 h-5 appearance-none bg-zinc-800 rounded-full relative cursor-pointer
                checked:bg-emerald-500
                before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-4 before:h-4 before:bg-white before:rounded-full before:transition-all
                checked:before:translate-x-4"
            />
          </label>
        </div>

        {/* Quick presets */}
        <div className="flex gap-1.5 flex-wrap mb-4">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 self-center mr-1">Voreinstellungen:</span>
          {PRESETS.map(p => (
            <button
              key={p.name}
              type="button"
              onClick={() => applyPreset(p)}
              data-testid={`smtp-preset-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className="px-2.5 py-1 text-[11px] bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-blue-500/40 text-zinc-300 rounded transition-colors"
            >
              {p.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Host" required>
            <input type="text" value={cfg.host} onChange={(e) => patch("host", e.target.value)}
              placeholder="smtp.office365.com" data-testid="smtp-host"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none" />
          </Field>
          <Field label="Port" required>
            <input type="number" value={cfg.port || ""} onChange={(e) => patch("port", parseInt(e.target.value || "0", 10))}
              placeholder="587" data-testid="smtp-port"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none" />
          </Field>
          <Field label="Verschlüsselung" required>
            <select value={cfg.encryption || "tls"} onChange={(e) => patch("encryption", e.target.value)}
              data-testid="smtp-encryption"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none">
              {ENC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="Absender-Name">
            <input type="text" value={cfg.from_name || ""} onChange={(e) => patch("from_name", e.target.value)}
              placeholder="MUNJA Trans GmbH" data-testid="smtp-from-name"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none" />
          </Field>

          <Field label="Absender-Adresse (Aus)" required>
            <input type="email" value={cfg.from_email || ""} onChange={(e) => patch("from_email", e.target.value)}
              placeholder="office@munja-trans.at" data-testid="smtp-from-email"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none" />
          </Field>
          <Field label="Benutzername (oft = E-Mail)">
            <input type="text" value={cfg.username || ""} onChange={(e) => patch("username", e.target.value)}
              placeholder="office@munja-trans.at" data-testid="smtp-username" autoComplete="username"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none" />
          </Field>
          <Field label={`Passwort${cfg.password_set ? " (gespeichert)" : ""}`} icon={<Lock className="w-3 h-3" />}>
            <input type="password" value={cfg.password || ""} onChange={(e) => patch("password", e.target.value)}
              placeholder={cfg.password_set ? "••••••••  (leer = unverändert)" : "Passwort"} data-testid="smtp-password" autoComplete="new-password"
              className="w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none" />
          </Field>
          <div className="text-[11px] text-zinc-500 flex items-end pb-2">
            <div>
              <strong>Hinweis:</strong> Für Gmail und Office365 muss ein <em>App-Passwort</em> erstellt werden (nicht das normale Login-Passwort).
            </div>
          </div>
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`mt-4 px-3 py-2.5 border rounded text-xs flex items-start gap-2 ${
            testResult.ok
              ? "bg-emerald-500/5 border-emerald-500/30 text-emerald-200"
              : "bg-red-500/5 border-red-500/30 text-red-200"
          }`} data-testid="smtp-test-result">
            {testResult.ok
              ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />}
            <div>
              {testResult.ok
                ? <><strong>Verbindung erfolgreich.</strong> Authentifizierung mit {testResult.host}:{testResult.port} ({testResult.encryption?.toUpperCase()}) hat geklappt.</>
                : <><strong>Verbindung fehlgeschlagen.</strong> {testResult.error}</>}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-zinc-800">
          <button onClick={save} disabled={saving} data-testid="smtp-save-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-900 text-white rounded text-sm font-medium transition-colors">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </button>
          <button onClick={testConnection} disabled={testing || !cfg.host} data-testid="smtp-test-conn-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-zinc-100 border border-zinc-700 rounded text-sm font-medium transition-colors">
            {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Verbindung testen
          </button>
          <div className="flex-1 min-w-[260px] flex gap-2">
            <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="empfaenger@beispiel.at  (Test-Mail)"
              data-testid="smtp-test-to"
              className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none" />
            <button onClick={sendTestEmail} disabled={sending || !cfg.host || !testTo} data-testid="smtp-test-send-btn"
              className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-900 text-white rounded text-sm font-medium transition-colors whitespace-nowrap">
              {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Test-Mail senden
            </button>
          </div>
        </div>

        {!cfg.enabled && cfg.host && (
          <div className="mt-3 flex items-start gap-2 px-3 py-2.5 bg-amber-500/5 border border-amber-500/30 rounded text-xs text-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>SMTP ist konfiguriert aber <strong>deaktiviert</strong>. E-Mails werden weiter über Resend versendet (falls konfiguriert). Aktivieren Sie SMTP oben, um es scharf zu schalten.</div>
          </div>
        )}
      </section>

      {/* ── Meldung Email Einstellungen ──────────────────────────── */}
      <RecipientList
        title="Meldung E-Mail Einstellungen"
        subtitle="Empfänger für Alarme: Pickerl, §57a, Versicherung, Führerschein, etc."
        kind="meldung"
        items={recipients.meldung}
        newValue={newMeldung}
        setNewValue={setNewMeldung}
        onAdd={() => addRecipient("meldung", newMeldung)}
        onRemove={(v) => removeRecipient("meldung", v)}
      />

      {/* ── Tanken Email Einstellungen ────────────────────────────── */}
      <RecipientList
        title="Tanken E-Mail Einstellungen"
        subtitle="Empfänger für Tank-Belege und Tankkarten-Benachrichtigungen."
        kind="tanken"
        items={recipients.tanken}
        newValue={newTanken}
        setNewValue={setNewTanken}
        onAdd={() => addRecipient("tanken", newTanken)}
        onRemove={(v) => removeRecipient("tanken", v)}
      />
    </div>
  );
}

function Field({ label, required, icon, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1 mb-1">
        {icon}
        <span>{label}</span>
        {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function RecipientList({ title, subtitle, kind, items, newValue, setNewValue, onAdd, onRemove }) {
  return (
    <section data-testid={`recipients-${kind}`}>
      <div className="mb-3 pb-2 border-b border-zinc-800">
        <h3 className="text-sm font-semibold text-blue-400">{title}</h3>
        <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>
      </div>
      <div className="flex gap-2 mb-3">
        <input
          type="email"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
          placeholder="E-Mail-Adresse"
          data-testid={`recipients-${kind}-input`}
          className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 focus:border-blue-500 outline-none"
        />
        <button
          onClick={onAdd}
          data-testid={`recipients-${kind}-add`}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" /> Hinzufügen
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-zinc-500 px-3 py-3 bg-zinc-950/50 border border-zinc-800/50 rounded">
          Keine Empfänger eingetragen.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">E-Mail</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-24">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e} className="border-b border-zinc-800/50 hover:bg-zinc-900/30" data-testid={`recipients-${kind}-row-${e}`}>
                <td className="px-3 py-2 text-zinc-200 font-mono text-xs">{e}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => onRemove(e)}
                    data-testid={`recipients-${kind}-remove-${e}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
